import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePurchaseOrderAccess } from '../hooks/usePurchaseOrderAccess';
import { useAuth } from '../context/AuthContext';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import {
  LayoutDashboard,
  Clock,
  Users,
  Droplets,
  Leaf,
  TreePine,
  DollarSign,
  UserCog,
  FlaskConical,
  Wrench,
  ClipboardList,
  Shield,
  Sprout,
  Store,
  Headphones,
  PauseCircle,
  Award,
  HardHat,
  Receipt,
  Apple,
  ClipboardCheck,
  BookOpenCheck,
  IdCard,
  Landmark,
  ShoppingCart,
} from 'lucide-react';
import { isIOSApp, isNativePlatform } from '../utils/platform';
import { isOrgAdmin, isOrgSuperAdmin, hasBillingAccess } from '../utils/auth';
import CreateOrganization from '../components/admin/CreateOrganizationForm';
import LeaveBalancesCard from '../components/LeaveBalancesCard';
import EquipmentQuickCard from '../components/EquipmentQuickCard';
import WeatherCard from '../components/WeatherCard';
import AccountCard from '../components/AccountCard';
import EmailVerificationBanner from '../components/auth/EmailVerificationBanner';
import timesheetService, { type TimesheetSummary } from '../services/timesheet';
import leaveService from '../services/leave';
import timesheetApprovalService from '../services/timesheetApproval';
import subscriptionService from '../services/subscription';
import jobService from '../services/jobs';
import harvestService from '../services/harvest';
import type { JobListItem } from '../types/jobs';
import { useOrgTimezone, formatDateInTz } from '../utils/timezone';
import { formatDuration } from '../utils/time';
import type { ActiveModule, UserLicense } from '../types/subscription';

function Dashboard() {
  const { user, refreshUser } = useAuth();
  const orgTimezone = useOrgTimezone();
  const [hasSkippedOrgSetup, setHasSkippedOrgSetup] = useState(false);
  const [summary, setSummary] = useState<TimesheetSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState<number>(0);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [activeModules, setActiveModules] = useState<ActiveModule[]>([]);
  const [myLicenses, setMyLicenses] = useState<UserLicense[]>([]);
  const [pausedJobs, setPausedJobs] = useState<JobListItem[]>([]);
  // Pickers hold no Farm Jobs seat; this is how they find "Start bins".
  const [pickingJobCount, setPickingJobCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();

  useAppRefresh(useCallback(() => setRefreshTick(t => t + 1), []));

  const hasModule = (key: string) => subscriptionService.hasModuleAccess(activeModules, key);
  const hasLicense = (key: string) =>
    subscriptionService.hasLicense(myLicenses, key, user?.organisation_id);
  const hasLicenseType = (key: string, typeKey: string) =>
    subscriptionService.hasLicenseType(myLicenses, key, typeKey, user?.organisation_id);
  const hasAnyLicenseType = (key: string, typeKeys: string[]) =>
    subscriptionService.hasAnyLicenseType(myLicenses, key, typeKeys, user?.organisation_id);

  // Operational RBAC — read from the active org membership rather than
  // deprecated user.roles. See utils/auth.ts.
  const isAdmin = isOrgAdmin(user);
  const isSuperAdmin = isOrgSuperAdmin(user);
  // Paid-tier gates. Leave + Timesheet share the same subscription, and a
  // single `approver` tier covers both timesheet and leave approvals.
  const hasApprover = hasAnyLicenseType('timesheet', ['approver', 'hr']);
  const hasTimesheetHr = hasLicenseType('timesheet', 'hr');
  const hasTimesheetPayroll = hasLicenseType('timesheet', 'payroll');
  const hasAnySprayLicense = hasLicense('spray');
  const hasAnyJobsLicense = hasLicense('farm_jobs');
  // Purchase orders: only the people the organisation's admin has authorised to
  // raise them get the card. An Accounts seat reads them from the Accounts
  // dashboard's Purchasing section. No seat decides it, so the server is asked.
  const { canRaise: canPurchaseOrders } = usePurchaseOrderAccess();
  // Compliance & audit: each section needs the seat that manages its records.
  const canCompliance = hasLicenseType('spray', 'admin') || hasTimesheetHr
    || hasAnyLicenseType('farm_jobs', ['admin', 'supervisor']);

  // Derived from summary once loaded — avoids a separate profile fetch
  const isFortnightly = summary?.pay_frequency === 'fortnightly';

  useEffect(() => {
    if (user?.organisation_name) {
      document.title = `Dashboard - ${user.organisation_name}`;
    } else {
      document.title = 'Dashboard - FactreeTech';
    }
  }, [user?.organisation_name]);

  useEffect(() => {
    if (!user?.organisation_id) return;
    setActiveModules([]);
    setMyLicenses([]);
    Promise.all([
      subscriptionService.getActiveModules(),
      subscriptionService.getMyLicenses(),
    ]).then(([modulesRes, licensesRes]) => {
      if (modulesRes.success && modulesRes.data) setActiveModules(modulesRes.data);
      if (licensesRes.success && licensesRes.data) setMyLicenses(licensesRes.data);
    }).catch(() => {});
  }, [user?.organisation_id, refreshTick]);

  useEffect(() => {
    if (!user?.organisation_id || !subscriptionService.hasModuleAccess(activeModules, 'farm_jobs')) return;
    harvestService.myJobs().then(j => setPickingJobCount(j.length)).catch(() => setPickingJobCount(0));
  }, [user?.organisation_id, activeModules, refreshTick]);

  // Let the backend determine the correct period window based on the user's pay_frequency
  useEffect(() => {
    setSummary(null);
    const fetchSummary = async () => {
      try {
        setLoadingSummary(true);
        const response = await timesheetService.getSummary({ period: 'week' });
        if (response.success) {
          setSummary(response.data.summary);
        }
      } catch (error: any) {
        console.error('Failed to fetch summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchSummary();
  }, [user?.organisation_id, refreshTick]);

  // Surface paused jobs assigned to the current operator so they can resume
  // them deliberately. The tracking controller no longer auto-restarts GPS on
  // app boot — this banner replaces that behaviour with an explicit nudge.
  useEffect(() => {
    setPausedJobs([]);
    // No seat check: a quick job from a pre-start is anyone's, and the server
    // narrows a seatless reader's list to their own.
    if (!user?.id || !user?.organisation_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await jobService.getJobs({
          assigned_to: user.id,
          status: 'paused',
          limit: 5,
        });
        if (cancelled) return;
        if (res.success && res.data?.jobs) setPausedJobs(res.data.jobs);
      } catch {
        /* ignore — banner is informational */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.organisation_id, refreshTick]);

  useEffect(() => {
    setPendingApprovalsCount(0);
    const fetchPendingApprovals = async () => {
      // Only fetch counts if the user actually holds an approver-level license.
      const canApprove = hasApprover;
      if (!canApprove) {
        return;
      }

      try {
        setLoadingApprovals(true);
        const [leaveResponse, timesheetResponse] = await Promise.all([
          leaveService.getPendingApprovalsCount(),
          timesheetApprovalService.getPendingApprovalsCount()
        ]);
        const leaveCount = leaveResponse.success ? leaveResponse.data.count : 0;
        const timesheetCount = timesheetResponse.success ? timesheetResponse.data.count : 0;
        setPendingApprovalsCount(leaveCount + timesheetCount);
      } catch (error: any) {
        console.error('Failed to fetch pending approvals:', error);
      } finally {
        setLoadingApprovals(false);
      }
    };
    fetchPendingApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisation_id, myLicenses]);

  const handleOrganizationCreated = async () => {
    await refreshUser();
  };

  // Organisation creation is hidden on iOS (App Store 3.1.1).
  if (!user?.organisation_id && !hasSkippedOrgSetup && !isIOSApp()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <CreateOrganization
            onSuccess={handleOrganizationCreated}
            onSkip={() => setHasSkippedOrgSetup(true)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PageHeader
        title="Dashboard"
        icon={<LayoutDashboard className="h-6 w-6 text-white" />}
        showUserInfo={true}
        showLogoutButton={true}
        showSystemStatus={false}
        showBackButton={false}
        showDashboardButton={false}
      />

      <EmailVerificationBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        {pausedJobs.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <PauseCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {pausedJobs.length === 1 ? 'You have a paused job' : `You have ${pausedJobs.length} paused jobs`}
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                GPS tracking is off until you tap Resume on the job.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {pausedJobs.map((j) => (
                  <button
                    key={j.id}
                    onClick={() => navigate(`/jobs/${j.id}/operate`)}
                    className="text-xs font-medium bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 rounded-full px-3 py-1 truncate max-w-[16rem]"
                    title={j.name}
                  >
                    {j.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <Card className="hover:shadow-xl transition-all duration-300 mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {/* Timesheet — always available. Every authenticated user can
                  clock in/out, view their hours, submit for approval. No
                  subscription or license required. */}
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 hover:bg-primary-50 hover:border-primary-200"
                onClick={() => navigate('/timesheet')}
              >
                <Clock className="h-6 w-6 text-primary-600" />
                <span className="text-sm font-medium">Timesheet</span>
              </Button>

              {/* Equipment — every authenticated user can view equipment and
                  run pre-start checks even without a subscription (same
                  pattern as timesheet). The Equipment Manager seat is only
                  needed to create/edit vehicles and manage maintenance. */}
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 hover:bg-amber-50 hover:border-amber-200"
                onClick={() => navigate('/equipment')}
              >
                <Wrench className="h-6 w-6 text-amber-600" />
                <span className="text-sm font-medium">Equipment</span>
              </Button>

              {hasModule('irrigation') && hasLicense('irrigation') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-primary-50 hover:border-primary-200"
                  onClick={() => navigate('/irrigation')}
                >
                  <Droplets className="h-6 w-6 text-primary-600" />
                  <span className="text-sm font-medium">Sensors & Irrigation</span>
                </Button>
              )}
              {/* Cultivar Registry — covers both the registry and license
                  management. Shown when the org has the cultivar subscription
                  and the user holds a cultivar license. */}
              {hasModule('cultivar') && hasLicense('cultivar') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-success-50 hover:border-success-200"
                  onClick={() => navigate('/cultivar')}
                >
                  <Leaf className="h-6 w-6 text-success-600" />
                  <span className="text-sm font-medium">Cultivar Registry</span>
                </Button>
              )}

              {/* Farms — free spatial setup. No subscription or license
                  required. Everyone can create and edit farms, blocks and
                  cultivar areas. Spraying and job assignment still need
                  their own subscriptions. */}
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 hover:bg-green-50 hover:border-green-200"
                onClick={() => navigate('/orchard')}
              >
                <TreePine className="h-6 w-6 text-green-600" />
                <span className="text-sm font-medium">Farms</span>
              </Button>
              {hasModule('spray') && hasAnySprayLicense && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-200"
                  onClick={() => navigate('/spray')}
                >
                  <FlaskConical className="h-6 w-6 text-emerald-600" />
                  <span className="text-sm font-medium">Spray</span>
                </Button>
              )}
              {canPurchaseOrders && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-indigo-50 hover:border-indigo-200"
                  onClick={() => navigate('/purchase-orders')}
                >
                  <ShoppingCart className="h-6 w-6 text-indigo-600" />
                  <span className="text-sm font-medium">Purchase Orders</span>
                </Button>
              )}
              {hasModule('farm_jobs') && pickingJobCount > 0 && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-rose-50 hover:border-rose-200"
                  onClick={() => navigate('/harvest/pick')}
                >
                  <Apple className="h-6 w-6 text-rose-600" />
                  <span className="text-sm font-medium">Start bins</span>
                </Button>
              )}
              {hasModule('farm_jobs') && hasAnyJobsLicense && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-indigo-50 hover:border-indigo-200"
                  onClick={() => navigate('/jobs')}
                >
                  <ClipboardList className="h-6 w-6 text-indigo-600" />
                  <span className="text-sm font-medium">Farm Jobs</span>
                </Button>
              )}

              {hasModule('nursery') && hasLicense('nursery') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-green-50 hover:border-green-200"
                  onClick={() => navigate('/nursery')}
                >
                  <Sprout className="h-6 w-6 text-green-600" />
                  <span className="text-sm font-medium">Nursery</span>
                </Button>
              )}

              {/* Accounts — customers, invoices, receivables, and the Xero / MYOB connections. */}
              {hasModule('accounts') && hasLicense('accounts') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-sky-50 hover:border-sky-200"
                  onClick={() => navigate('/accounts')}
                >
                  <Landmark className="h-6 w-6 text-sky-600" />
                  <span className="text-sm font-medium">Accounts</span>
                </Button>
              )}

              {/* Retail — a retail nursery's shop: counter, stock, tills, prices. */}
              {hasModule('retail') && hasLicense('retail') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-200"
                  onClick={() => navigate('/retail')}
                >
                  <Store className="h-6 w-6 text-emerald-600" />
                  <span className="text-sm font-medium">Retail</span>
                </Button>
              )}

              {/* Sales — calls from the phone system, against the customers. */}
              {hasModule('sales') && hasLicense('sales') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-violet-50 hover:border-violet-200"
                  onClick={() => navigate('/sales')}
                >
                  <Headphones className="h-6 w-6 text-violet-600" />
                  <span className="text-sm font-medium">Sales</span>
                </Button>
              )}

              {hasModule('breeder') && hasLicense('breeder') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-amber-50 hover:border-amber-200"
                  onClick={() => navigate('/breeder')}
                >
                  <Award className="h-6 w-6 text-amber-600" />
                  <span className="text-sm font-medium">Breeder</span>
                </Button>
              )}

              {/* Organisation Admin — admin / super_admin RBAC roles. */}
              {isAdmin && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-purple-50 hover:border-purple-200"
                  onClick={() => navigate('/admin')}
                >
                  <Users className="h-6 w-6 text-purple-600" />
                  <span className="text-sm font-medium">Organisation Admin</span>
                </Button>
              )}

              {/* Module Store — web only for App Store / Play Store compliance. */}
              {isAdmin && !isNativePlatform() && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-teal-50 hover:border-teal-200"
                  onClick={() => navigate('/subscriptions/purchase')}
                >
                  <Store className="h-6 w-6 text-teal-600" />
                  <span className="text-sm font-medium">Module Store</span>
                </Button>
              )}

              {/* Super Admin — super_admin only. */}
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-amber-50 hover:border-amber-200"
                  onClick={() => navigate('/super-admin')}
                >
                  <Shield className="h-6 w-6 text-amber-600" />
                  <span className="text-sm font-medium">Super Admin</span>
                </Button>
              )}

              {/* Billing console — billing_manager or super_admin, web only. */}
              {hasBillingAccess(user) && !isNativePlatform() && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-indigo-50 hover:border-indigo-200"
                  onClick={() => navigate('/billing')}
                >
                  <Receipt className="h-6 w-6 text-indigo-600" />
                  <span className="text-sm font-medium">Billing</span>
                </Button>
              )}

              {/* Report a safety issue — available to ANY worker in an org that
                  has the HR module (ISO 45001 worker participation), not just
                  HR-seat holders. */}
              {hasModule('timesheet') && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-orange-50 hover:border-orange-200"
                  onClick={() => navigate('/whs/report')}
                >
                  <HardHat className="h-6 w-6 text-orange-600" />
                  <span className="text-sm font-medium">Report a Safety Issue</span>
                </Button>
              )}

              {/* Inductions — everyone: the policies they have to read and what
                  they are signed off to operate. One card: whoever signs
                  people off, or runs the pack, finds the register in a menu
                  on that page. */}
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 hover:bg-lime-50 hover:border-lime-200"
                onClick={() => navigate('/my-inductions')}
              >
                <BookOpenCheck className="h-6 w-6 text-lime-700" />
                <span className="text-sm font-medium">Inductions</span>
              </Button>

              {/* Certifications — everyone records their own licences and
                  tickets. One card: an HR seat gets the staff register from a
                  button on that page. */}
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 hover:bg-teal-50 hover:border-teal-200"
                onClick={() => navigate('/certifications')}
              >
                <IdCard className="h-6 w-6 text-teal-700" />
                <span className="text-sm font-medium">Certifications</span>
              </Button>

              {/* Compliance & audit — season packs, auditor access, self-assessment. */}
              {canCompliance && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-primary-50 hover:border-primary-200"
                  onClick={() => navigate('/compliance')}
                >
                  <ClipboardCheck className="h-6 w-6 text-primary-600" />
                  <span className="text-sm font-medium">Compliance &amp; Audit</span>
                </Button>
              )}

              {/* HR and payroll — one card for the Timesheet HR and Payroll
                  seats. The page it opens shows each half only to its seat. */}
              {(hasTimesheetHr || hasTimesheetPayroll) && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2 hover:bg-rose-50 hover:border-rose-200"
                  onClick={() => navigate('/hr')}
                >
                  {hasTimesheetHr
                    ? <UserCog className="h-6 w-6 text-rose-600" />
                    : <DollarSign className="h-6 w-6 text-emerald-600" />}
                  <span className="text-sm font-medium">
                    {!hasTimesheetHr ? 'Payroll' : hasTimesheetPayroll ? 'HR & Payroll' : 'HR Management'}
                  </span>
                </Button>
              )}

            </div>
          </CardContent>
        </Card>

        {/* Stat Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8 animate-fade-in">
          {/* Pay Cycle Summary */}
          <Card
            className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
            onClick={() => navigate('/timesheet')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-lg text-neutral-600">Pay Cycle Summary</CardTitle>
                {summary?.period_start && summary?.period_end && (
                  <CardDescription>
                    {formatDateInTz(summary.period_start, orgTimezone)} – {formatDateInTz(summary.period_end, orgTimezone)}
                  </CardDescription>
                )}
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {loadingSummary ? (
                <div className="text-neutral-400 text-sm">Loading…</div>
              ) : !summary || summary.total_sessions === 0 ? (
                <div className="flex flex-col items-center justify-center py-4 text-neutral-400">
                  <p className="text-sm font-medium text-neutral-500">No hours this period</p>
                  <p className="text-xs mt-1">Clock in to start tracking</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-50 px-3 py-2 rounded-lg">
                      <div className="text-lg font-bold text-blue-600">{formatDuration(summary.total_hours)}</div>
                      <div className="text-xs text-blue-600">Hours Worked</div>
                    </div>
                    <div className="bg-green-50 px-3 py-2 rounded-lg">
                      <div className="text-lg font-bold text-green-600">{formatDuration(summary.scheduled_hours)}</div>
                      <div className="text-xs text-green-600">Scheduled</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-purple-50 px-3 py-2 rounded-lg">
                      <div className="text-lg font-bold text-purple-600">{summary.total_days_worked}</div>
                      <div className="text-xs text-purple-600">Days Worked</div>
                    </div>
                    <div className={`px-3 py-2 rounded-lg ${summary.overtime_hours > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                      <div className={`text-lg font-bold ${summary.overtime_hours > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {summary.overtime_hours > 0 ? formatDuration(summary.overtime_hours) : '—'}
                      </div>
                      <div className={`text-xs ${summary.overtime_hours > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {isFortnightly ? 'TIL Accrued' : 'Overtime'}
                      </div>
                    </div>
                  </div>
                  {summary.public_holiday_days > 0 && (
                    <div className="bg-rose-50 px-3 py-2 rounded-lg flex items-center justify-between">
                      <div className="text-xs text-rose-700">
                        {summary.public_holidays.map((ph: { date: string; name: string }) => ph.name).join(', ')}
                      </div>
                      <div className="text-sm font-bold text-rose-600 ml-2 flex-shrink-0">{formatDuration(summary.public_holiday_hours)}</div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {hasApprover && (
            <Card
              className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate('/approvals')}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-lg text-neutral-600">Pending Approvals</CardTitle>
                  {loadingApprovals ? (
                    <p className="text-3xl font-bold text-neutral-400">--</p>
                  ) : (
                    <p className="text-3xl font-bold text-blue-600">{pendingApprovalsCount}</p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </CardHeader>
            </Card>
          )}

          <LeaveBalancesCard />

          <EquipmentQuickCard />

          <WeatherCard />

          <AccountCard />
        </div>

        <div className="mt-8 text-center text-xs text-neutral-500">
          <span className="font-medium">Version</span> {__APP_VERSION__}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
